
import React, { useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, Radio, Chip } from '@mui/material';
import { Base_Url, b2bCreateShipment } from '../../../config/config';
import { toast } from 'react-toastify';
import {
  RiMoneyRupeeCircleFill,
  RiTruckLine,
  RiCalculatorLine,
  RiBarChartBoxLine,
  RiShieldCheckLine,
  RiTimeLine
} from 'react-icons/ri';
import {
  IoIosCloseCircle,
  IoMdCheckmarkCircleOutline
} from 'react-icons/io';
import '../../css/custom-scrollbar.css';
import { useNavigate } from 'react-router-dom';
import { FiTrendingUp, FiPackage, FiMapPin } from 'react-icons/fi';
import FullPageLoader from '../../fullPageLoader/fullPageLoader';
import { useMyContext } from '../../../context/MyContext';

const B2BViewRateForward = ({ isopen, onClose, data, shipment, type }) => {
 
  const {isDarkMode} = useMyContext()
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedOption, setSelectedOption] = useState('comparison'); // 'breakdown' or 'comparison'
  const [selectedPartner, setSelectedPartner] = useState('');
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const token = localStorage.getItem('token');
  const cusid = localStorage.getItem('cus_id');
  const navigate = useNavigate();
  const [isPageLoading, setIsPageLoading] = useState(false)

  // Always call hooks in the same order - use data with fallback
  const responseData = data || { data: {} };

  // Safe access to nested data with fallback
  const partners = Object.keys(responseData?.data || {});

  // Get the first partner's data for header info
  const firstPartnerData = partners.length > 0 ? responseData?.data?.[partners[0]] || {} : {};

  // Current selected partner data
  const currentPartner = selectedPartner || partners[0];
  const currentPartnerData = responseData?.data?.[currentPartner] || {};

  // Format charge breakdown data for selected partner
  const chargeBreakdownData = useMemo(() => {
    if (!currentPartnerData?.charges_breakdown) return [];

    const breakdown = currentPartnerData.charges_breakdown;
    const breakdownArray = Object.entries(breakdown)
      .filter(([key, charge]) => charge?.value > 0) // Only show charges with value > 0
      .map(([key, charge]) => ({
        id: key,
        label: charge?.label || key,
        value: charge?.value || 0,
        type: 'charge'
      }));

    // Add summary rows
    return [
      ...breakdownArray,
      {
        id: 'estimated_total',
        label: 'Estimated Charge (Before GST)',
        value: currentPartnerData?.estimated_charge || 0,
        type: 'subtotal'
      },
      {
        id: 'final_total',
        label: 'Final Amount (With GST)',
        value: currentPartnerData?.final_amount_with_gst || 0,
        type: 'total'
      }
    ];
  }, [currentPartnerData]);

  // Partner comparison data
  const comparisonData = useMemo(() => {
    if (!responseData?.data) return [];

    return partners.map(partner => {
      const partnerData = responseData.data[partner] || {};
      return {
        id: partner,
        name: partner,
        zone: partnerData?.zone || 'N/A',
        estimatedCharge: partnerData?.estimated_charge || 0,
        finalAmount: partnerData?.final_amount_with_gst || 0,
        ratePerKg: partnerData?.rate_per_kg || 0,
        chargeableWeight: partnerData?.chargeable_weight || 0,
        totalCharges: Object.keys(partnerData?.charges_breakdown || {}).length,
        type: 'partner',
        ...partnerData
      };
    });
  }, [responseData?.data, partners]);


  const handleOptionChange = (option) => {
    setSelectedOption(option);
  };

  const handlePartnerChange = (partner) => {
    setSelectedPartner(partner);
  };

  const handleRadioChange = (event, row) => {
    setSelectedRowId(row.id);
    setSelectedRowData(row);
  };


  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "N/A";
    return `₹ ${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getRowClassName = (item) => {
    if (item?.type === 'total') {
      return "border-b-2 border-t-2 border-emerald-500 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900 dark:to-green-900 font-bold text-lg";
    } else if (item?.type === 'subtotal') {
      return "border-b border-t border-blue-400 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900 dark:to-indigo-900 font-semibold";
    }
    return "border-b border-gray-200 dark:border-gray-600 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 dark:hover:from-gray-800 dark:hover:to-blue-900 transition-all duration-200";
  };

  const getBestDeal = () => {
    if (comparisonData.length === 0) return null;
    return comparisonData.reduce((best, current) =>
      (current?.finalAmount || 0) < (best?.finalAmount || 0) ? current : best
    );
  };

  const bestDeal = getBestDeal();

  const handleClose = () => {
    onClose();
  };



  const createShipment = async () => {
    const payload = {
      order_id: shipment.order_id,
      shipment_type: "forword",
      partner: selectedRowData.id,
    };
    setIsPageLoading(true);
    setIsSubmitting(true);
    try {
      const response = await fetch(Base_Url + b2bCreateShipment, {
        method: 'POST',
        headers: {
          'cus_id': cusid,
          'content-type': 'application/json',
          'token': token,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result?.status === "SUCCESS" || result?.data?.status === "SUCCESS") {
        toast.success(result?.message || result?.data?.message || "Shipment created successfully");
        navigate('/b2b/dashboard');
        setIsSubmitting(false);
        setIsPageLoading(false);
        handleClose();
      } else {
        const errorMessage =
          result?.message ||
          result?.data?.message ||
          result?.messages?.error ||   
          "Something went wrong";
        toast.error(errorMessage);
        setIsSubmitting(false);
        setIsPageLoading(false);
      }
    } catch (error) {
      toast.error("API Error: " + error.message);
      setIsSubmitting(false);
      setIsPageLoading(false);
    }
  }

  return (
    <>
      <Dialog
        open={isopen}
        onClose={handleClose}
        maxWidth="xl"
        sx={{
          '& .MuiDialog-paper': {
            width: '50%',
            maxWidth: '1200px',
            height: '70vh',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          }
        }}
      >
        <DialogTitle className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-blue-600/20 to-indigo-600/20 backdrop-blur-md"></div>
          <div className="relative z-10">
            <h2 className="text-md font-bold flex items-center justify-center gap-3">
              <div className="p-2 bg-white/20 rounded-full">
                <RiCalculatorLine size={24} />
              </div>
              Rate Calculator
            </h2>
            <p className="text-center text-blue-100 text-sm mt-1">
              Compare rates and analyze shipping costs across multiple partners
            </p>
          </div>
          <IoIosCloseCircle
            className="absolute top-4 right-4 cursor-pointer hover:text-gray-200 transition-colors z-20 hover:rotate-90 transition-transform duration-300"
            size={24}
            onClick={onClose}
          />
        </DialogTitle>

        <DialogContent
          className={`shadow-xl rounded-b-2xl p-0  custom-scrollbar
        ${isDarkMode ? "dark:bg-darkmodeColor text-white" : "bg-gradient-to-br from-gray-50 to-blue-50"}`}
          sx={{ overflowY: 'auto', padding: 0 }}
        >
          {/* Header Info Section */}
          {firstPartnerData && (
            <div className="p-4 bg-gradient-to-r from-indigo-100 via-purple-50 to-pink-100 dark:from-indigo-900 dark:via-purple-900 dark:to-pink-900 border-b">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-500 text-white rounded-xl">
                    <FiMapPin size={12} />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Zone</span>
                    <p className="text-xs font-semibold text-blue-600">{firstPartnerData.zone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-500 text-white rounded-xl">
                    <FiPackage size={12} />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Weight</span>
                    <p className="text-xs font-bold text-green-600">{firstPartnerData.chargeable_weight} kg</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-500 text-white rounded-xl">
                    <RiMoneyRupeeCircleFill size={12} />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Rate/kg</span>
                    <p className="text-xs font-bold text-purple-600">₹{firstPartnerData.rate_per_kg}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-orange-500 text-white rounded-xl">
                    <RiBarChartBoxLine size={12} />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Invoice Value</span>
                    <p className="text-xs font-bold text-orange-600">{formatCurrency(firstPartnerData.invoice_value)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-red-500 text-white rounded-xl">
                    <RiTruckLine size={12} />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Partners</span>
                    <p className="text-xs font-bold text-red-600">{partners.length}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="p-4">
            {/* Toggle Buttons */}
            <div className="mb-4 flex gap-3 bg-white dark:bg-gray-800 p-2 rounded-xl shadow-lg">
              <button
                className={`flex-1 text-sm px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${selectedOption === 'comparison'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transform scale-105'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                onClick={() => handleOptionChange('comparison')}
              >
                <FiTrendingUp size={16} />
                Partner Comparison
              </button>
              <button
                className={`flex-1 text-sm px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${selectedOption === 'breakdown'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transform scale-105'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                onClick={() => handleOptionChange('breakdown')}
              >
                <RiCalculatorLine size={16} />
                Detailed Breakdown
              </button>
            </div>

            {/* Partner Selection for Breakdown */}
            {selectedOption === 'breakdown' && partners.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xs font-semibold mb-3 text-gray-700 dark:text-gray-300">Select Partner</h3>
                <div className="flex flex-wrap gap-3">
                  {partners.map(partner => (
                    <button
                      key={partner}
                      className={`px-3 py-1 text-sm rounded-xl font-medium transition-all duration-300 transform hover:scale-105 ${currentPartner === partner
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:border-emerald-500'
                        }`}
                      onClick={() => handlePartnerChange(partner)}
                    >
                      <div className="flex items-center gap-2">
                        <RiTruckLine size={12} />
                        {partner}
                        {bestDeal?.name === partner && (
                          <Chip
                            label="Best Deal"
                            size="small"
                            className="ml-2 bg-yellow-400 text-yellow-900 font-bold"
                          />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
   
            {/* Content based on selected option */}
            <div className=" text-xs bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
              {selectedOption === 'breakdown' && chargeBreakdownData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
                      <tr>
                        <th className="p-3 text-left font-bold text-xs">Charge Description</th>
                        <th className="p-3 text-right font-bold text-xs">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chargeBreakdownData.map((item) => (
                        <tr key={item.id} className={getRowClassName(item)}>
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              {item.type === 'total' && <IoMdCheckmarkCircleOutline className="text-emerald-500" size={24} />}
                              {item.type === 'subtotal' && <RiShieldCheckLine className="text-blue-500" size={20} />}
                              <span className={`${item.type === 'total' ? 'text-xs font-bold text-emerald-700' : item.type === 'subtotal' ? 'text-lg font-semibold text-blue-700' : 'text-gray-700 dark:text-gray-300'}`}>
                                {item.label}
                              </span>
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <span className={`${item.type === 'total'
                              ? 'text-xs font-bold text-emerald-600 bg-emerald-100 px-4 py-2 rounded-lg'
                              : item.type === 'subtotal'
                                ? 'text-xs font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-lg'
                                : 'text-gray-700 dark:text-gray-300'
                              }`}>
                              {formatCurrency(item.value)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : selectedOption === 'comparison' && comparisonData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white">
                      <tr>
                        <th className="p-2 text-center">Select</th>
                        <th className="p-2 text-left">Partner</th>
                        <th className="p-2 text-center">Zone</th>
                        <th className="p-2 text-center">Rate/kg</th>
                        <th className="p-2 text-center">Base Charge</th>
                        <th className="p-2 text-center">Total Charges</th>
                        <th className="p-2 text-center">Final Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.map((row) => (
                        <tr
                          key={row.id}
                          className={`border-b border-gray-200 dark:border-gray-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-blue-900 dark:hover:to-indigo-900 transition-all duration-200 ${bestDeal?.name === row.name ? 'bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900 dark:to-green-900 border-emerald-200' : ''
                            }`}
                        >
                          <td className="p-2 text-center">
                            <Radio
                              checked={selectedRowId === row.id}
                              onChange={(event) => handleRadioChange(event, row)}
                              value={row.id}
                              name="selectRow"
                              className="text-blue-600"
                            />
                          </td>
                          <td className="p-2">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg">
                                <RiTruckLine size={20} />
                              </div>
                              <div>
                                <div className="font-bold text-xs text-gray-800 dark:text-gray-200 capitalize">
                                  {row.name}
                                  {bestDeal?.name === row.name && (
                                    <Chip
                                      label="Best Deal"
                                      size="small"
                                      className="ml-2 bg-yellow-400 text-yellow-900 font-bold"
                                    />
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {row.totalCharges} charges applied
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="p-2 text-center">
                            <Chip
                              label={row.zone}
                              className="bg-blue-100 text-blue-800 font-semibold"
                            />
                          </td>
                          <td className="p-2 text-center font-semibold text-purple-600">
                            ₹{row.ratePerKg}
                          </td>
                          <td className="p-2 text-center font-semibold">
                            {formatCurrency(row.estimatedCharge)}
                          </td>
                          <td className="p-2 text-center">
                            <Chip
                              label={row.totalCharges}
                              className="bg-gray-100 text-gray-800"
                            />
                          </td>
                          <td className="p-2 text-center">
                            <div className={`font-bold text-xs px-3 py-2 rounded-lg ${bestDeal?.name === row.name
                              ? 'text-emerald-700 bg-emerald-100'
                              : 'text-blue-700 bg-blue-100'
                              }`}>
                              {formatCurrency(row.finalAmount)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="text-gray-400">
                    <RiCalculatorLine size={64} className="mx-auto mb-6 opacity-30" />
                    <h3 className="text-xs font-semibold mb-2">No Rate Data Available</h3>
                    <p className="text-xs">Please check your request parameters and try again.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Summary Section */}
            {selectedOption === 'breakdown' && currentPartnerData && (
              <div className="mt-6 p-3 bg-gradient-to-r from-emerald-100 via-green-100 to-teal-100 dark:from-emerald-900 dark:via-green-900 dark:to-teal-900 rounded-2xl shadow-lg border border-emerald-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xs font-bold text-emerald-700 dark:text-emerald-300 mb-2">
                      {currentPartner} - Total Cost
                    </h3>
                    <p className="text-emerald-600 text-xs dark:text-emerald-400">
                      *Including all applicable charges and GST
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-emerald-700 dark:text-emerald-300">
                      {formatCurrency(currentPartnerData.final_amount_with_gst)}
                    </div>
                    <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                      Base: {formatCurrency(currentPartnerData.estimated_charge)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* {selectedOption === 'comparison' && bestDeal && (
              <div className="mt-6 p-2 bg-gradient-to-r from-yellow-100 via-orange-100 to-red-100 dark:from-yellow-900 dark:via-orange-900 dark:to-red-900 rounded-2xl shadow-lg border border-yellow-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xs font-bold text-orange-700 dark:text-orange-300 mb-2 flex items-center gap-2">
                      <RiShieldCheckLine size={24} />
                      Best Deal: {bestDeal.name}
                    </h3>
                    <p className=" ml-6 font-semibold text-xs text-orange-600 dark:text-orange-400">
                      Lowest total cost among all partners
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-orange-700 dark:text-orange-300">
                      {formatCurrency(bestDeal.finalAmount)}
                    </div>
                    <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                      Save vs highest: {formatCurrency(Math.max(...comparisonData.map(p => p.finalAmount)) - bestDeal.finalAmount)}
                    </div>
                  </div>
                </div>
              </div>
            )} */}
          </div>
        </DialogContent>

        <div className="flex justify-between items-center p-6 bg-gradient-to-r from-gray-100 to-blue-100 dark:from-gray-800 dark:to-blue-900 border-t">
          <div className="text-xs text-gray-600 dark:text-gray-400">
            <RiTimeLine className="inline mr-1" />
            Rates calculated in real-time
          </div>
          {type !== "checkRates" && (
            <button
              className="px-8 py-3 bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 hover:from-purple-700 hover:via-blue-700 hover:to-indigo-700 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-3"
              disabled={isSubmitting || (selectedOption === "comparison" && !selectedRowId)}
              onClick={createShipment}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </div>
              ) : (
                <>
                  <RiTruckLine size={20} />
                  {type === "reverse" ? "Create Reverse Shipment" : "Create Shipment"}
                </>
              )}
            </button>
          )}

        </div>
      </Dialog>
      <FullPageLoader
        isLoading={isPageLoading}
        message="Processing your request..."
      />    
    </>

  );
};

export default B2BViewRateForward;